import * as React from 'react';
import { useEffect } from 'react';

import { isCourserian } from 'js/lib/user';

import { isSSR } from 'bundles/common/utils/isSSR';
import CourseraToolbarErrorBoundary from 'bundles/coursera-toolbar/components/CourseraToolbarErrorBoundary';
import { ToolbarSettingsProvider } from 'bundles/coursera-toolbar/context/ToolbarSettingsContext';
import { isLocalDev } from 'bundles/coursera-toolbar/utils/environmentUtils';
import { persistCourserianToolbarFlag, shouldMountToolbar } from 'bundles/coursera-toolbar/utils/toolbarSession';

// Lazy load the CourseraToolbar component and plugins
const CourseraToolbar = React.lazy(() =>
  Promise.all([
    import('bundles/coursera-toolbar/components/CourseraToolbar'),
    import('bundles/coursera-toolbar/plugins/PluginRegistry'),
    import('bundles/coursera-toolbar/plugins/epic'),
    import('bundles/coursera-toolbar/plugins/statsig-overrides'),
    import('bundles/coursera-toolbar/plugins/rendering'),
    import('bundles/coursera-toolbar/plugins/asg'),
    import('bundles/coursera-toolbar/plugins/previews'),
    import('bundles/coursera-toolbar/plugins/environment-switcher'),
    import('bundles/coursera-toolbar/plugins/settings'),
    import('bundles/coursera-toolbar/plugins/cds-colors'),
    import('bundles/coursera-toolbar/plugins/worktree-switcher'),
    import('bundles/coursera-toolbar/plugins/custom-headers'),
  ])
    .then(
      ([
        toolbarModule,
        registryModule,
        epicModule,
        statsigOverridesModule,
        renderingModule,
        asgModule,
        previewsModule,
        environmentSwitcherModule,
        settingsModule,
        cdsColorsModule,
        worktreeSwitcherModule,
        customHeadersModule,
      ]) => {
        const registry = registryModule.default;
        const epicPlugin = epicModule.default;
        const statsigOverridesPlugin = statsigOverridesModule.default;
        const renderingPlugin = renderingModule.default;
        const asgPlugin = asgModule.default;
        const previewsPlugin = previewsModule.default;
        const environmentSwitcherPlugin = environmentSwitcherModule.default;
        const settingsPlugin = settingsModule.default;
        const cdsColorsPlugin = cdsColorsModule.default;
        const worktreeSwitcherPlugin = worktreeSwitcherModule.default;
        const customHeadersPlugin = customHeadersModule.default;
        registry.register(epicPlugin);
        registry.register(statsigOverridesPlugin);
        registry.register(renderingPlugin);
        registry.register(asgPlugin);
        registry.register(previewsPlugin);
        registry.register(environmentSwitcherPlugin);
        registry.register(settingsPlugin);
        registry.register(cdsColorsPlugin);
        registry.register(worktreeSwitcherPlugin);
        registry.register(customHeadersPlugin);

        return {
          default: toolbarModule.default,
        };
      }
    )
    .catch((error) => {
      throw error;
    })
);

// Lazy load the SSR Fallback banner
const SSRFallbackBanner = React.lazy(() => import('bundles/coursera-toolbar/components/SSRFallbackBanner'));

/**
 * Lazy wrapper for CourseraToolbar. Loads for local dev, logged-in Courserians, or logged-out users
 * with a prior persisted eligibility flag in localStorage (see toolbarSession).
 */

const LazyCourseraToolbar = () => {
  const courserianLoggedIn = isCourserian();

  useEffect(() => {
    if (courserianLoggedIn) {
      persistCourserianToolbarFlag();
    }
  }, [courserianLoggedIn]);

  // Render the SSR fallback if we're in SSR render mode
  const showSSRFallback = isSSR() && !isLocalDev();

  if (!shouldMountToolbar()) {
    return null;
  }

  if (showSSRFallback) {
    return (
      <CourseraToolbarErrorBoundary>
        <React.Suspense fallback={null}>
          <SSRFallbackBanner />
        </React.Suspense>
      </CourseraToolbarErrorBoundary>
    );
  }

  return (
    <CourseraToolbarErrorBoundary>
      <ToolbarSettingsProvider>
        <React.Suspense fallback={null}>
          <CourseraToolbar />
        </React.Suspense>
      </ToolbarSettingsProvider>
    </CourseraToolbarErrorBoundary>
  );
};

export default LazyCourseraToolbar;
